import type { ReactNode } from 'react'
import { Button } from '@/components/Button'
import { SplitView, SplitViewMain, SplitViewSide } from '@/components/SplitView'
import { SplitViewSidebarListItem } from '@/components/SplitView/SplitViewSidebarListItem'
import { ScrollView } from '@/components/ScrollView'
import { SearchField } from '@/components/SearchField'
import { Spinner } from '@/components/Spinner'
import styles from './CustomerLookupSection.module.css'
import { Form } from '@/components/Form'

export interface CustomerLookupResult {
  key: string
  displayName: string
  email: string
  phoneNumber: string
  active: boolean
  onSelect: () => void
}

export interface CustomerLookupSectionProps {
  searchText: string
  onSearchTextChange: (text: string) => unknown
  onSubmitSearch: () => unknown
  showAddClientButton: boolean
  onAddClient: () => void
  isSearching: boolean
  showEmptyState: boolean
  hasLastQuery: boolean
  showResults: boolean
  results: CustomerLookupResult[]
  resultsLoader?: ReactNode
  hasSelection: boolean
  details: ReactNode
}

export function CustomerLookupSection({
  searchText,
  onSearchTextChange,
  onSubmitSearch,
  showAddClientButton,
  onAddClient,
  isSearching,
  showEmptyState,
  hasLastQuery,
  showResults,
  results,
  resultsLoader,
  hasSelection,
  details,
}: CustomerLookupSectionProps) {
  return (
    <div className={styles.root}>
      <Form className={styles.lookupHeader} onSubmit={() => onSubmitSearch()}>
        <div className={styles.search}>
          <SearchField
            appearance="plain"
            stretch
            className={styles.searchField}
            value={searchText}
            onChange={onSearchTextChange}
            placeholder="Search by name, email, or phone"
          />
        </div>
        {showAddClientButton && (
          <Button label="Add client" variant="primary" onPress={onAddClient} />
        )}
      </Form>

      <SplitView flexChild>
        <SplitViewSide narrow>
          <div className={styles.sidebarContent}>
            {isSearching && (
              <div className={styles.spinnerContainer}>
                <Spinner size={20} />
              </div>
            )}
            {showEmptyState && (
              <div className={styles.emptyState}>
                {hasLastQuery
                  ? 'No matching clients were found. Try a different search or add a new client.'
                  : 'Start by searching for a client.'}
              </div>
            )}
            {showResults && (
              <ScrollView>
                <div className={styles.resultsList}>
                  {results.map((result) => (
                    <ResultListItem key={result.key} result={result} />
                  ))}
                  {resultsLoader}
                </div>
              </ScrollView>
            )}
          </div>
        </SplitViewSide>

        <SplitViewMain>
          {hasSelection ? (
            details
          ) : (
            <div className={styles.placeholder}>
              Select a contact to view their details
            </div>
          )}
        </SplitViewMain>
      </SplitView>
    </div>
  )
}

interface ResultListItemProps {
  result: CustomerLookupResult
}

function ResultListItem({ result }: ResultListItemProps) {
  const { displayName, email, phoneNumber } = result

  return (
    <SplitViewSidebarListItem
      active={result.active}
      onPress={result.onSelect}
      spacing="compact"
    >
      <div className={styles.resultContainer}>
        <div className={styles.resultName}>
          {displayName || email || 'Unknown'}
        </div>
        {email && <div className={styles.resultContactInfo}>{email}</div>}
        {phoneNumber && (
          <div className={styles.resultContactInfo}>{phoneNumber}</div>
        )}
      </div>
    </SplitViewSidebarListItem>
  )
}
